import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import api from '../../config/api';
import './ContactSupport.css';

const ContactSupport = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchTickets = async () => {
    try {
      const res = await api.get('/web-user/tickets');
      const tickets = res.data?.tickets || res.data || [];
      
      const parsedMessages = [];

      // Add default welcome message
      parsedMessages.push({
        id: 'welcome',
        text: "Hello! How can our support team help you today?",
        sender: "support",
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      });

      tickets.forEach(ticket => {
        // User message
        parsedMessages.push({
          id: ticket._id,
          text: ticket.message || ticket.subject,
          sender: "user",
          time: new Date(ticket.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        });

        // Admin replies if any
        if (ticket.reply && Array.isArray(ticket.reply)) {
          ticket.reply.forEach((rep, idx) => {
            parsedMessages.push({
              id: `${ticket._id}-reply-${idx}`,
              text: rep.message,
              sender: rep.by === 'admin' ? "support" : "user",
              time: new Date(rep.createdAt || ticket.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            });
          });
        }
      });

      setMessages(parsedMessages);
    } catch (err) {
      console.error("Error fetching support tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    const interval = setInterval(fetchTickets, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || sending) return;

    const messageText = inputText.trim();
    setInputText('');
    setSendError('');
    setSending(true);

    const tempMessage = {
      id: Date.now(),
      text: messageText,
      sender: "user",
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      await api.post('/web-user/tickets', {
        subject: messageText.length > 30 ? messageText.substring(0, 30) + '...' : messageText,
        message: messageText
      });
      fetchTickets();
    } catch (err) {
      console.error("Failed to send support ticket:", err);
      setSendError(err.response?.data?.message || 'Failed to send message. Please try again.');
      setMessages((prev) => prev.filter((msg) => msg.id !== tempMessage.id));
      setInputText(messageText);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="support-chat-container">
      <header className="support-header">
        <button onClick={() => navigate(-1)} className="back-btn">
          <ArrowLeft size={24} />
        </button>
        <h1>Contact Support</h1>
        <div style={{ width: 40 }}></div>
      </header>

      <div className="chat-content">
        <div className="date-divider">
          <span>Today</span>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4 py-4">
            <div className="flex justify-start"><div className="h-10 w-48 bg-gray-200 rounded-2xl rounded-bl-sm"></div></div>
            <div className="flex justify-end"><div className="h-10 w-56 bg-red-100 rounded-2xl rounded-br-sm"></div></div>
            <div className="flex justify-start"><div className="h-12 w-64 bg-gray-200 rounded-2xl rounded-bl-sm"></div></div>
          </div>
        ) : (

          messages.map((msg) => (
            <div key={msg.id} className={`message-row ${msg.sender}`}>
              <div className="message-bubble">
                {msg.text}
              </div>
              <span className="message-time">{msg.time}</span>
            </div>
          ))
        )}

        <div ref={chatEndRef} />
      </div>

      {sendError && (
        <div className="chat-send-error" role="alert">
          {sendError}
        </div>
      )}

      <form className="chat-input-wrapper" onSubmit={handleSendMessage}>
        <input 
          type="text" 
          placeholder="Type Message..." 
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          disabled={sending}
        />
        <button type="submit" className="send-btn" disabled={sending}>
          <Send size={20} fill="#fff" />
        </button>
      </form>
    </div>
  );
};

export default ContactSupport;
